import { useState, type FormEvent } from 'react';
import {
  useSchedules, useSaveSchedule, useDeleteTms, useBusinessHours, useNominateBusinessHours,
} from '../../lib/api/hooks-tms.js';
import { useSession } from '../../lib/auth/session.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, SubHead, SectionHead, EditorHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import {
  DAYS, HoursEditor, blankHours, hhmm, hoursFromRules, invalidDay, rulesFromHours, type HoursDraft,
} from '../../components/telephony/HoursEditor.js';
import type { Schedule } from '@arcnet/contracts';
import { confirmAction } from '../../components/ui/Confirm.js';

/**
 * Business hours and holidays.
 *
 * Modelled as a WEEK plus a list of exceptions rather than a flat rule list,
 * because that is the shape of the thing being described. A flat list makes
 * "are we open on Tuesday?" a question you answer by reading every row and
 * working out which one wins.
 *
 * The editor itself is shared with an inbound route's hours of its own
 * (components/telephony/HoursEditor.tsx), so the two cannot drift.
 */
export function SchedulesTab({ tenantId }: { tenantId: string }) {
  const schedules = useSchedules(tenantId);
  const del = useDeleteTms(tenantId, 'schedules');
  const business = useBusinessHours(tenantId);
  const nominate = useNominateBusinessHours(tenantId);
  const can = useSession((s) => s.can);
  // Creating, editing and deleting need extension:write, as the API requires (permission sweep, 19 September 2026).
  const mayWrite = can('extension:write');
  const mayNominate = can('tenant:write');
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [creating, setCreating] = useState(false);

  // An editor takes the list's place, so there is one thing on the page (owner, 29 September 2026).
  if (creating || editing) {
    return (
      <>
      <EditorHead back="Hours & schedules" onBack={() => { setCreating(false); setEditing(null); }}
        title={editing ? editing.name : 'New schedule'} />
      <ScheduleEditor
        key={editing?.id ?? 'new'}
        tenantId={tenantId} existing={editing}
        onDone={() => { setCreating(false); setEditing(null); }}
      />
      </>
    );
  }

  return (
    <>
      <SectionHead title="Hours & schedules"
        sub="Opening hours and holidays. One is the account's business hours; an inbound route can follow any of them."
        action={mayWrite && (
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating(true); setEditing(null); }}>
          <Icon name="plus" size={13} />
          New schedule
        </button>
        )} />

      {schedules.error && <ErrorBanner error={schedules.error} />}


      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Name</th><th>Open hours</th>
                <th className="arc-td-num">Exceptions</th><th>Right now</th><th />
              </tr>
            </thead>
            {schedules.data === undefined ? <TableSkeleton cols={5} /> : (
              <tbody>
                {schedules.data?.data.map((sc) => (
                  <tr key={sc.id} className="arc-row-editable" onDoubleClick={() => { setEditing(sc); setCreating(false); }}>
                    <td style={{ fontWeight: 550 }}>
                      {sc.name}
                      {business.data?.schedule_id === sc.id && <> <span className="arc-subtle" style={{ fontWeight: 400 }}>business hours</span></>}
                    </td>
                    <td className="arc-muted">{summariseWeek(sc)}</td>
                    <td className="arc-td-num">
                      {sc.rules.filter((r) => r.kind !== 'weekly').length || (
                        <span className="arc-subtle">—</span>
                      )}
                    </td>
                    <td><OpenNow schedule={sc} /></td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        {mayNominate && business.data && business.data.schedule_id !== sc.id && (
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            disabled={nominate.isPending} onClick={() => nominate.mutate(sc.id)}>
                            Make business hours
                          </button>
                        )}
                        {mayWrite && (
                        <RowAction kind="edit" label={`Edit ${sc.name}`}
                          onClick={() => { setEditing(sc); setCreating(false); }} />
                        )}
                        {mayWrite && (
                        <RowAction kind="delete" label={`Delete ${sc.name}`}
                          onClick={() => {
                            void confirmAction({ title: `Delete schedule '${sc.name}'?`, action: 'Delete', danger: true })
                              .then((ok) => { if (ok) del.mutate(sc.id); });
                          }} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {schedules.data !== undefined && schedules.data?.data.length === 0 && (
          <Empty icon="clock" title="No schedules"
            hint="A schedule lets an inbound route send after-hours calls somewhere else." />
        )}
      </div>
    </>
  );
}

/**
 * Answers the only question anyone actually asks of a schedule.
 *
 * Evaluated in the BROWSER's clock, not the schedule's timezone, so it is
 * labelled as such rather than presented as authoritative. The switch decides
 * for real; this is a sanity check for the person editing.
 */
function OpenNow({ schedule }: { schedule: Schedule }) {
  const now = new Date();
  const now5 = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const today = now.toISOString().slice(0, 10);
  const dow = now.getDay();

  // Highest priority wins, which is what makes a holiday beat the week.
  const matched = [...schedule.rules]
    .sort((a, b) => b.priority - a.priority)
    .find((r) => {
      if (r.kind === 'weekly') {
        if (!r.days_of_week?.includes(dow)) return false;
        return !r.time_from || !r.time_to
          || (now5 >= hhmm(r.time_from, '00:00') && now5 < hhmm(r.time_to, '24:00'));
      }
      const from = r.date_from ?? '';
      const to = r.date_to ?? r.date_from ?? '';
      return today >= from && today <= to;
    });

  if (!matched) return <Badge tone="muted">closed</Badge>;
  return (
    <Badge tone={matched.is_open ? 'ok' : 'muted'}>
      <span className="arc-dot" aria-hidden="true" />
      {matched.is_open ? 'open' : 'closed'}
      {matched.label ? ` · ${matched.label}` : ''}
    </Badge>
  );
}

function summariseWeek(sc: Schedule): string {
  const weekly = sc.rules.filter((r) => r.kind === 'weekly' && r.is_open);
  if (weekly.length === 0) return 'never open';
  // Group days that share the same hours so the summary stays one line.
  const byHours = new Map<string, number[]>();
  for (const r of weekly) {
    const k = `${hhmm(r.time_from, '00:00')}–${hhmm(r.time_to, '24:00')}`;
    for (const d of r.days_of_week ?? []) byHours.set(k, [...(byHours.get(k) ?? []), d]);
  }
  return [...byHours.entries()]
    .map(([hours, days]) => {
      const names = DAYS.filter((d) => days.includes(d.n)).map((d) => d.short);
      return `${names.join(', ')} ${hours}`;
    })
    .join(' · ');
}

function ScheduleEditor({ tenantId, existing, onDone }: {
  tenantId: string; existing: Schedule | null; onDone: () => void;
}) {
  const save = useSaveSchedule(tenantId);
  const [name, setName] = useState(existing?.name ?? '');
  const [timezone, setTimezone] = useState(existing?.timezone ?? 'Africa/Johannesburg');
  const [hours, setHours] = useState<HoursDraft>(
    () => (existing ? hoursFromRules(existing.rules) : blankHours()));

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          name,
          timezone: timezone || null,
          rules: rulesFromHours(hours),
        }, { onSuccess: onDone });
      }}
    >

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="sc-name">Name</label>
          <input id="sc-name" className="arc-input" required value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Office hours" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="sc-tz">Timezone</label>
          <input id="sc-tz" className="arc-input" value={timezone}
            onChange={(e) => setTimezone(e.target.value)} placeholder="Africa/Johannesburg" />
          <p className="arc-hint">Hours are interpreted here, not in the caller's timezone.</p>
        </div>
      </div>

      <HoursEditor idPrefix="sc-hours" value={hours} onChange={setHours} />

      <button type="submit" className="arc-btn arc-btn-primary"
        disabled={save.isPending || invalidDay(hours) !== undefined}>
        {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create schedule'}
      </button>
    </form>
  );
}
